/**
 * src/components/teacher-dashboard-components/theme/components/Avatar.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';

const AvatarContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: ${props => {
    switch (props.$size) {
      case 'xs': return '1.5rem';
      case 'sm': return '2rem';
      case 'md': return '2.5rem';
      case 'lg': return '4rem';
      case 'xl': return '5rem';
      case '2xl': return '6rem';
      default: return '2.5rem';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'xs': return '1.5rem';
      case 'sm': return '2rem';
      case 'md': return '2.5rem';
      case 'lg': return '4rem';
      case 'xl': return '5rem';
      case '2xl': return '6rem';
      default: return '2.5rem';
    }
  }};
  border-radius: ${props => props.$shape === 'square' ? props.theme.borderRadius.md : props.theme.borderRadius.full};
  object-fit: cover;
  border: ${props => props.$bordered ? `2px solid ${getColor('neutral.medium')}` : 'none'};
  box-shadow: ${props => props.$shadow ? props.theme.shadows.sm : 'none'};
  ${createTransition('all', 'fast')};
  
  &:hover {
    ${props => props.$interactive && `
      transform: scale(1.05);
      box-shadow: ${props.theme.shadows.md};
    `}
  }
`;

const AvatarFallback = styled.div`
  width: ${props => {
    switch (props.$size) {
      case 'xs': return '1.5rem';
      case 'sm': return '2rem';
      case 'md': return '2.5rem';
      case 'lg': return '4rem';
      case 'xl': return '5rem';
      case '2xl': return '6rem';
      default: return '2.5rem';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'xs': return '1.5rem';
      case 'sm': return '2rem';
      case 'md': return '2.5rem';
      case 'lg': return '4rem';
      case 'xl': return '5rem';
      case '2xl': return '6rem';
      default: return '2.5rem';
    }
  }};
  border-radius: ${props => props.$shape === 'square' ? props.theme.borderRadius.md : props.theme.borderRadius.full};
  background: ${props => {
    switch (props.$variant) {
      case 'primary': return getColor('primary.blue');
      case 'secondary': return getColor('primary.skyBlue');
      case 'success': return getColor('semantic.success');
      case 'warning': return getColor('semantic.warning');
      case 'error': return getColor('semantic.error');
      default: return getColor('neutral.medium');
    }
  }};
  color: ${getColor('neutral.white')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => {
    switch (props.$size) {
      case 'xs': return '0.5rem';
      case 'sm': return '0.75rem';
      case 'md': return '1rem';
      case 'lg': return '1.5rem';
      case 'xl': return '2rem';
      case '2xl': return '2.5rem';
      default: return '1rem';
    }
  }};
  font-weight: 600;
  text-transform: uppercase;
  border: ${props => props.$bordered ? `2px solid ${getColor('neutral.medium')}` : 'none'};
  box-shadow: ${props => props.$shadow ? props.theme.shadows.sm : 'none'};
  ${createTransition('all', 'fast')};
  
  &:hover {
    ${props => props.$interactive && `
      transform: scale(1.05);
      box-shadow: ${props.theme.shadows.md};
    `}
  }
`;

const AvatarBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${props => {
    switch (props.$size) {
      case 'xs': return '0.375rem';
      case 'sm': return '0.5rem';
      case 'md': return '0.625rem';
      case 'lg': return '1rem';
      case 'xl': return '1.25rem';
      case '2xl': return '1.5rem';
      default: return '0.625rem';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'xs': return '0.375rem';
      case 'sm': return '0.5rem';
      case 'md': return '0.625rem';
      case 'lg': return '1rem';
      case 'xl': return '1.25rem';
      case '2xl': return '1.5rem';
      default: return '0.625rem';
    }
  }};
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => {
    switch (props.$status) {
      case 'online': return getColor('semantic.success');
      case 'away': return getColor('semantic.warning');
      case 'busy': return getColor('semantic.error');
      case 'offline': return getColor('neutral.dark');
      default: return getColor('neutral.medium');
    }
  }};
  border: 2px solid ${getColor('neutral.white')};
`;

const ActionButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${props => {
    switch (props.$size) {
      case 'xs': return '1rem';
      case 'sm': return '1.25rem';
      case 'md': return '1.5rem';
      case 'lg': return '2rem';
      case 'xl': return '2.5rem';
      case '2xl': return '3rem';
      default: return '1.5rem';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'xs': return '1rem';
      case 'sm': return '1.25rem';
      case 'md': return '1.5rem';
      case 'lg': return '2rem';
      case 'xl': return '2.5rem';
      case '2xl': return '3rem';
      default: return '1.5rem';
    }
  }};
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${getColor('primary.blue')};
  color: ${getColor('neutral.white')};
  border: 2px solid ${getColor('neutral.white')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.shadows.sm};
  ${createTransition('all', 'fast')};
  
  &:hover {
    background: ${getColor('primary.lightBlue')};
    transform: scale(1.1);
  }
  
  &:focus {
    outline: 2px solid ${getColor('primary.skyBlue')};
    outline-offset: 2px;
  }
`;

/**
 * Avatar.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Avatar = ({
  src,
  alt = '',
  size = 'md',
  shape = 'circle',
  variant = 'neutral',
  fallback,
  name,
  bordered = false,
  shadow = false,
  interactive = false,
  status,
  actionIcon,
  onAction,
  className,
  onClick,
  ...props
}) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  
    /**
   * Get Fallback Text.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const getFallbackText = () => {
    if (fallback) return fallback;
    if (name) {
      const names = name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`;
      }
      return names[0][0];
    }
    return '?';
  };
  
    /**
   * Handle Image Error.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleImageError = () => {
    setImageError(true);
  };
  
  const avatarContent = src && !imageError ? (
    <AvatarImage
      theme={theme}
      src={src}
      alt={alt}
      $size={size}
      $shape={shape}
      $bordered={bordered}
      $shadow={shadow}
      $interactive={interactive}
      onError={handleImageError}
      onClick={onClick}
    />
  ) : (
    <AvatarFallback
      theme={theme}
      $size={size}
      $shape={shape}
      $variant={variant}
      $bordered={bordered}
      $shadow={shadow}
      $interactive={interactive}
      onClick={onClick}
    >
      {getFallbackText()}
    </AvatarFallback>
  );
  
  return (
    <AvatarContainer theme={theme} className={className} {...props}>
      {avatarContent}
      
      {status && (
        <AvatarBadge theme={theme} $size={size} $status={status} />
      )}
      
      {actionIcon && onAction && (
        <ActionButton theme={theme} $size={size} onClick={onAction}>
          {actionIcon}
        </ActionButton>
      )}
    </AvatarContainer>
  );
};

export default Avatar;
